/**
 * Subscribes to workflow lock and status WebSocket events and updates the reports
 * cache and React Query cache in place (no refetch where possible).
 * Delegates in-place cache updates to `reportCacheUpdates`.
 * Gated by feature flag V3-UI-REACTS-TO-WEBSOCKET-WORKFLOW-EVENTS (default off).
 */

import { useQueryClient, type QueryClient } from "react-query";
import { useAppContext } from "../AppProviders";
import { useFamaFlags } from "../api/FamaFlags";
import { CacheKeys, type ApplicationCache } from "../api/Cache";
import { ReportSummaryItemApi } from "../api/Reports";
import { isActorUuidMatch } from "../api/Users";
import {
  isStepNameInUiMap,
  normalizeStepNameForUi,
  WORKFLOW_STATUS_ENUM,
} from "../api/Workflows";
import { useStorageMap } from "../storage/Storage";
import { useWebSocketEvent } from "../services/websocket/useWebSocketEvent";
import { cacheUpdateEvents } from "../services/websocket/cacheUpdateEvents";
import { threeSixtyAssigneeOverlay } from "../services/websocket/threeSixtyAssigneeOverlay";
import {
  applyReportCacheUpdate,
  invalidateReportsInProcessQueriesContainingDueDate,
  removeReportFromAllCaches,
  removeReportFromAllCachesIncludingCompleted,
  updateLatestWorkflowStepRunCache,
  type ApplyReportCacheUpdateOptions,
  type ReportUpdateFn,
} from "../services/websocket/reportCacheUpdates";
import { isHandledWorkflowRunName } from "../services/websocket/workflowRunNameAllowlist";
import { useActorInfoQuery } from "../api/UsersApi";
import {
  WORKFLOW_WEBSOCKET_EVENT,
  type WorkflowLockedEventData,
  type WorkflowUnlockedEventData,
  type WorkflowLockFailedEventData,
  type WorkflowStatusEventData,
  type WorkflowCompletedEventData,
  type WorkflowCancelledEventData,
  type WorkflowFailedEventData,
  type WorkflowStartedEventData,
  type ReportDeletedEventData,
} from "../services/websocket/connectionManager";

/**
 * Tries in-place cache + React Query update for the matching report row.
 * Does not refetch list pages on cache miss — patch-style events only affect rows
 * already in cache. Use workflow_started invalidation when a new row may appear.
 */
function applyReportCacheUpdateWithMissHandling(
  cache: ApplicationCache,
  queryClient: QueryClient,
  args: {
    reportUuid: string | undefined;
    workflowRunUuid: string;
    updateFn: ReportUpdateFn;
    applyOptions: ApplyReportCacheUpdateOptions;
    onCacheMiss?: () => void;
  }
): void {
  const updated = applyReportCacheUpdate(
    cache,
    queryClient,
    args.reportUuid,
    args.workflowRunUuid,
    args.updateFn,
    args.applyOptions
  );
  if (updated) return;

  args.onCacheMiss?.();
}

export function useReportLockEvents(): void {
  const { cache } = useAppContext();
  const { updateCacheRefKey } = useStorageMap();
  const queryClient = useQueryClient();
  const { data: actorInfo } = useActorInfoQuery(true);
  const { v3UiReactsToWebsocketWorkflowEvents: workflowEventsEnabled } =
    useFamaFlags();

  const persistOptions = {
    onPersist: () => {
      updateCacheRefKey(CacheKeys.reports, cache.current.reports, false);
    },
  };

  const removalOptions = {
    ...persistOptions,
    onNotify: (reportUuid: string | undefined) => {
      cacheUpdateEvents.notify(reportUuid);
    },
  };

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.LOCKED,
    (data: WorkflowLockedEventData) => {
      const currentActorUuid = actorInfo?.actor_uuid;
      // Until our identity is known we can't tell our own lock from a foreign
      // one. Skip rather than risk patching our own lock as foreign (which
      // would flash a read-only / locked row on our own reports).
      if (!currentActorUuid) return;
      // Own lock: no list/cache patch (avoids showing locked to yourself).
      if (isActorUuidMatch(currentActorUuid, data.locked_by_actor_uuid)) {
        return;
      }
      const lockedByName = (data.locked_by_name ?? "").trim();
      if (lockedByName) {
        threeSixtyAssigneeOverlay.set(data.workflow_run_uuid, lockedByName);
      }
      applyReportCacheUpdateWithMissHandling(cache.current, queryClient, {
        reportUuid: data.report_uuid ?? undefined,
        workflowRunUuid: data.workflow_run_uuid,
        updateFn: (report: ReportSummaryItemApi) => ({
          ...report,
          is_locked: true,
          locked_by: data.locked_by_actor_uuid,
          locked_by_name: lockedByName,
        }),
        applyOptions: persistOptions,
        onCacheMiss: () =>
          cacheUpdateEvents.notify(data.report_uuid ?? undefined, {
            reload360Lists: false,
          }),
      });
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.UNLOCKED,
    (data: WorkflowUnlockedEventData) => {
      threeSixtyAssigneeOverlay.set(data.workflow_run_uuid, "");
      applyReportCacheUpdateWithMissHandling(cache.current, queryClient, {
        reportUuid: data.report_uuid ?? undefined,
        workflowRunUuid: data.workflow_run_uuid,
        updateFn: (report: ReportSummaryItemApi) => ({
          ...report,
          is_locked: false,
          locked_by: "",
          locked_by_name: "",
        }),
        applyOptions: persistOptions,
        onCacheMiss: () =>
          cacheUpdateEvents.notify(data.report_uuid ?? undefined, {
            reload360Lists: false,
          }),
      });
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.LOCK_FAILED,
    (data: WorkflowLockFailedEventData) => {
      const currentActorUuid = actorInfo?.actor_uuid;
      // Mirror the LOCKED handler: until our identity is known we can't tell a
      // self-held lock from a foreign one, so skip rather than risk marking our
      // own row locked. Don't mark it locked against ourselves either.
      if (!currentActorUuid) return;
      if (isActorUuidMatch(currentActorUuid, data.current_lock_actor_uuid)) {
        return;
      }
      applyReportCacheUpdateWithMissHandling(cache.current, queryClient, {
        reportUuid: data.report_uuid ?? undefined,
        workflowRunUuid: data.workflow_run_uuid,
        updateFn: (report: ReportSummaryItemApi) => ({
          ...report,
          is_locked: true,
          locked_by: data.current_lock_actor_uuid,
          locked_by_name: "",
        }),
        applyOptions: persistOptions,
      });
      // lock_failed carries no display name; refetch 360 lists so Assigned To shows the holder.
      cacheUpdateEvents.notify(data.report_uuid ?? undefined);
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.STATUS,
    (data: WorkflowStatusEventData) => {
      const stepName = data.current_step_name;
      const stepNameForUi =
        stepName != null && isStepNameInUiMap(stepName)
          ? stepName
          : stepName != null
          ? normalizeStepNameForUi(stepName)
          : null;
      if (stepNameForUi == null || !isStepNameInUiMap(stepNameForUi)) return;
      applyReportCacheUpdateWithMissHandling(cache.current, queryClient, {
        reportUuid: data.report_uuid ?? undefined,
        workflowRunUuid: data.workflow_run_uuid,
        updateFn: (report) => ({
          ...report,
          step_name: stepNameForUi,
          workflow_status: WORKFLOW_STATUS_ENUM.IN_PROGRESS,
        }),
        applyOptions: persistOptions,
      });
      // v2026 rows overlay step data from the per-row latest_step_run query (it wins over the
      // list row), so patch that cache too for the step change to render like the legacy list.
      updateLatestWorkflowStepRunCache(
        queryClient,
        data.workflow_run_uuid,
        stepNameForUi
      );
      void queryClient.invalidateQueries([
        "latestWorkflowStepRun",
        data.workflow_run_uuid,
      ]);
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.COMPLETED,
    (data: WorkflowCompletedEventData) => {
      if (!isHandledWorkflowRunName(data.workflow_run_name)) return;
      removeReportFromAllCaches(
        cache.current,
        queryClient,
        data.report_uuid ?? undefined,
        data.workflow_run_uuid,
        removalOptions
      );
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.CANCELLED,
    (data: WorkflowCancelledEventData) => {
      // No workflow_run_name allowlist: sub-workflows (e.g. 360, scrape-process) share report_uuid with the list row.
      removeReportFromAllCaches(
        cache.current,
        queryClient,
        data.report_uuid ?? undefined,
        data.workflow_run_uuid,
        removalOptions
      );
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.FAILED,
    (data: WorkflowFailedEventData) => {
      applyReportCacheUpdateWithMissHandling(cache.current, queryClient, {
        reportUuid: data.report_uuid ?? undefined,
        workflowRunUuid: data.workflow_run_uuid,
        updateFn: (report: ReportSummaryItemApi) => ({
          ...report,
          workflow_status: WORKFLOW_STATUS_ENUM.FAILED,
        }),
        applyOptions: persistOptions,
      });
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.STARTED,
    (data: WorkflowStartedEventData) => {
      if (!isHandledWorkflowRunName(data.workflow_run_name)) return;
      invalidateReportsInProcessQueriesContainingDueDate(
        queryClient,
        data.due_date ?? undefined
      );
    },
    { enabled: workflowEventsEnabled }
  );

  useWebSocketEvent(
    WORKFLOW_WEBSOCKET_EVENT.REPORT_DELETED,
    (data: ReportDeletedEventData) => {
      const workflowRunUuid = data.workflow_run_uuid ?? data.report_uuid ?? "";
      removeReportFromAllCachesIncludingCompleted(
        cache.current,
        queryClient,
        data.report_uuid ?? undefined,
        workflowRunUuid,
        removalOptions
      );
    },
    { enabled: workflowEventsEnabled }
  );
}
